import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
  getAiccKubeAutoscale,
  getAiccKubeWorkloads,
  restartAiccKubeWorkload,
  scaleAiccKubeWorkload,
} from 'api/prokey/prokey.axios';

const REFRESH_INTERVAL_MS = 15000;

function value(object, ...names) {
  for(const name of names) {
    if(object?.[name] !== undefined && object?.[name] !== null) return object[name];
  }
  return undefined;
}

function workloadKey(workload) {
  return `${value(workload, 'kind', 'Kind')}/${value(workload, 'name', 'Name')}`;
}

function compareWorkloadsByName(left, right) {
  const leftName = String(value(left, 'name', 'Name') ?? '');
  const rightName = String(value(right, 'name', 'Name') ?? '');
  return leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: 'base' });
}

function workloadOperations(workload) {
  const operations = value(workload, 'allowedOperations', 'AllowedOperations');
  if(!Array.isArray(operations)) return [];
  return operations.map(operation => String(operation).toLowerCase());
}

function workloadManagementLabel(workload, canScale, canRestart, min, max) {
  const mode = String(value(workload, 'managementMode', 'ManagementMode') ?? '').toLowerCase();
  const prefix = mode === 'autoscale' ? 'Autoscaled' : 'Manual';
  if(canScale) return `${prefix} · ${min}–${max}`;
  if(canRestart) return `${prefix} · Restart only`;
  return 'Inventory only';
}

function sameWorkloadValue(left, right) {
  return String(left ?? '').toLowerCase() === String(right ?? '').toLowerCase();
}

function findRule(rules, workload) {
  const kind = value(workload, 'kind', 'Kind');
  const name = value(workload, 'name', 'Name');
  return (rules || []).find(rule =>
    sameWorkloadValue(value(rule, 'kind', 'Kind', 'workloadKind', 'WorkloadKind'), kind) &&
    sameWorkloadValue(value(rule, 'name', 'Name', 'workloadName', 'WorkloadName'), name)
  );
}

function findState(state, rule) {
  if(!state || !rule) return null;
  const moduleName = value(rule, 'module', 'Module');
  if(moduleName && state[moduleName]) return state[moduleName];
  return Object.values(state).find(item => value(item, 'module', 'Module') === moduleName) || null;
}

function WorkloadMetric({ label, amount }) {
  return (
    <Box sx={{ minWidth: 0, textAlign: 'center' }}>
      <Typography component='div' variant='subtitle2' fontWeight={700} lineHeight={1.2}>{amount}</Typography>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
    </Box>
  );
}

export function AiccModulesPanel() {
  const [workloads, setWorkloads] = useState([]);
  const [autoscale, setAutoscale] = useState(null);
  const [replicas, setReplicas] = useState({});
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const refresh = useCallback(async (showLoading = false) => {
    if(showLoading) setLoading(true);
    try {
      const [workloadResponse, autoscaleResponse] = await Promise.all([
        getAiccKubeWorkloads(),
        getAiccKubeAutoscale(),
      ]);
      const nextWorkloads = [...(workloadResponse.data || [])].sort(compareWorkloadsByName);
      setWorkloads(nextWorkloads);
      setAutoscale(autoscaleResponse.data || null);
      setReplicas(current => {
        const next = { ...current };
        nextWorkloads.forEach(workload => {
          const key = workloadKey(workload);
          if(next[key] === undefined) next[key] = value(workload, 'replicas', 'Replicas') ?? 0;
        });
        return next;
      });
      setError('');
    } catch(requestError) {
      setError(requestError.response?.data?.message || 'Unable to load AICC module status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh(true);
    const interval = setInterval(() => refresh(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const rules = useMemo(() => value(autoscale, 'rules', 'Rules') || [], [autoscale]);
  const autoscaleState = useMemo(() => value(autoscale, 'state', 'State') || {}, [autoscale]);
  const managedCount = useMemo(() => workloads.filter(workload => workloadOperations(workload).length > 0).length, [workloads]);
  const autoscaleEnabled = value(autoscale, 'enabled', 'Enabled');

  async function runOperation(workload, operation, message) {
    const key = workloadKey(workload);
    setBusy(current => ({ ...current, [key]: true }));
    setError('');
    try {
      await operation();
      setNotice(message);
      await refresh(false);
    } catch(requestError) {
      setError(requestError.response?.data?.message || `Unable to update ${key}.`);
    } finally {
      setBusy(current => ({ ...current, [key]: false }));
    }
  }

  function requestScale(workload) {
    const key = workloadKey(workload);
    const desired = Number(replicas[key]);
    const current = value(workload, 'replicas', 'Replicas') ?? 0;
    const min = Number(value(workload, 'minReplicas', 'MinReplicas') ?? 0);
    const max = Number(value(workload, 'maxReplicas', 'MaxReplicas') ?? 100);
    if(!Number.isInteger(desired) || desired < min || desired > max) {
      setError(`Replica count must be an integer from ${min} through ${max}.`);
      return;
    }
    const execute = () => runOperation(
      workload,
      () => scaleAiccKubeWorkload(value(workload, 'kind', 'Kind'), value(workload, 'name', 'Name'), desired),
      `${key} scaled to ${desired} replica${desired === 1 ? '' : 's'}.`,
    );
    if(desired < current) {
      setConfirmation({ title: 'Confirm scale down', text: `Scale ${key} down from ${current} to ${desired} replicas?`, execute });
    } else {
      execute();
    }
  }

  function requestRestart(workload) {
    const key = workloadKey(workload);
    setConfirmation({
      title: 'Confirm restart',
      text: `Restart ${key}? Active work may be interrupted while its pods are replaced.`,
      execute: () => runOperation(
        workload,
        () => restartAiccKubeWorkload(value(workload, 'kind', 'Kind'), value(workload, 'name', 'Name')),
        `${key} restart requested.`,
      ),
    });
  }

  if(loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress aria-label='Loading AICC modules' /></Box>;

  return (
    <Stack
      data-testid='aicc-modules-panel'
      spacing={2}
      sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: 2, boxSizing: 'border-box' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
            <Typography variant='h6'>AICC Kubernetes Modules</Typography>
            <Chip
              size='small'
              color={autoscale === null ? 'default' : autoscaleEnabled ? 'success' : 'warning'}
              label={autoscale === null ? 'Status unavailable' : autoscaleEnabled ? 'Autoscaling enabled' : 'Autoscaling disabled'}
            />
          </Stack>
          <Typography variant='body2' color='text.secondary'>{managedCount} of {workloads.length} workloads managed by AICC</Typography>
        </Box>
        <Button size='small' variant='outlined' startIcon={<RefreshIcon />} onClick={() => refresh(true)}>Refresh</Button>
      </Box>
      {error && <Alert severity='error' onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity='success' onClose={() => setNotice('')}>{notice}</Alert>}
      {!workloads.length && !error && <Alert severity='info'>No configured AICC workloads were returned.</Alert>}
      <Box
        data-testid='aicc-workload-grid'
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: 1.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        {workloads.map(workload => {
          const key = workloadKey(workload);
          const rule = findRule(rules, workload);
          const state = findState(autoscaleState, rule);
          const operations = workloadOperations(workload);
          const canScale = operations.includes('scale');
          const canRestart = operations.includes('restart');
          const isManaged = canScale || canRestart;
          const min = Number(value(workload, 'minReplicas', 'MinReplicas') ?? 0);
          const max = Number(value(workload, 'maxReplicas', 'MaxReplicas') ?? 100);
          const managementLabel = workloadManagementLabel(workload, canScale, canRestart, min, max);
          const isBusy = Boolean(busy[key]);
          const kind = value(workload, 'kind', 'Kind');
          const name = value(workload, 'name', 'Name');
          const namespace = value(workload, 'namespace', 'Namespace');
          const displayName = value(workload, 'displayName', 'DisplayName') || name;
          const description = value(workload, 'description', 'Description') || '';
          const replicaCount = value(workload, 'replicas', 'Replicas') ?? 0;
          const readyCount = value(workload, 'readyReplicas', 'ReadyReplicas') ?? 0;
          const availableCount = value(workload, 'availableReplicas', 'AvailableReplicas') ?? 0;
          const updatedCount = value(workload, 'updatedReplicas', 'UpdatedReplicas') ?? 0;
          return (
            <Box key={key} sx={{ minWidth: 0 }}>
              <Card
                variant='outlined'
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isManaged ? 'primary.light' : 'divider',
                  transition: theme => theme.transitions.create(['border-color', 'box-shadow']),
                  '&:hover': { borderColor: isManaged ? 'primary.main' : 'text.disabled', boxShadow: 1 },
                }}
              >
                <CardHeader
                  sx={{ px: 2, pt: 1.75, pb: 1, '& .MuiCardHeader-content': { minWidth: 0 } }}
                  title={(
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                      <Typography variant='subtitle1' fontWeight={600} noWrap title={displayName} sx={{ flex: 1, minWidth: 0 }}>{displayName}</Typography>
                      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
                        {description && (
                          <Tooltip
                            arrow
                            placement='top'
                            title={description}
                            componentsProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                          >
                            <IconButton size='small' aria-label={`About ${displayName}`} sx={{ p: 0.25 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Chip size='small' variant='outlined' label={kind} sx={{ height: 22, fontSize: '0.7rem' }} />
                      </Stack>
                    </Stack>
                  )}
                  subheader={(
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography variant='caption' color='text.secondary' noWrap title={name}>{name}</Typography>
                      <Typography variant='caption' color='text.secondary' noWrap title={namespace}>{namespace}</Typography>
                    </Stack>
                  )}
                />
                <CardContent sx={{ px: 2, py: 1, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <WorkloadMetric label='Replicas' amount={replicaCount} />
                    <WorkloadMetric label='Ready' amount={readyCount} />
                    <WorkloadMetric label='Available' amount={availableCount} />
                    <WorkloadMetric label='Updated' amount={updatedCount} />
                  </Box>
                  <Stack direction='row' spacing={0.75} mt={1.25} flexWrap='wrap' useFlexGap>
                    <Chip size='small' color={isManaged ? 'primary' : 'default'} variant='outlined' label={managementLabel} />
                    {state && <Chip size='small' variant='outlined' label={`Queue · ${value(state, 'items', 'Items') ?? 0}`} />}
                    {state && <Chip size='small' variant='outlined' label={`Demand · ${value(state, 'effectiveItems', 'EffectiveItems') ?? 0}`} />}
                  </Stack>
                </CardContent>
                <CardActions sx={{ minHeight: 64, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', gap: 0.75, flexWrap: 'wrap' }}>
                  {isManaged ? <>
                    {canScale && <TextField
                      size='small'
                      type='number'
                      label='Replicas'
                      value={replicas[key] ?? ''}
                      inputProps={{ min, max, step: 1 }}
                      onChange={event => setReplicas(current => ({ ...current, [key]: event.target.value }))}
                      disabled={isBusy}
                      sx={{ width: 92 }}
                    />}
                    {canScale && <Button size='small' variant='contained' onClick={() => requestScale(workload)} disabled={isBusy} sx={{ height: 40 }}>Scale</Button>}
                    {canRestart && <Button size='small' color='warning' startIcon={<RestartAltIcon />} onClick={() => requestRestart(workload)} disabled={isBusy} sx={{ height: 40 }}>Restart</Button>}
                    {isBusy && <CircularProgress size={22} />}
                  </> : <Typography variant='caption' color='text.secondary'>Visible for health monitoring only</Typography>}
                </CardActions>
              </Card>
            </Box>
          );
        })}
      </Box>
      <Dialog open={Boolean(confirmation)} onClose={() => setConfirmation(null)}>
        <DialogTitle>{confirmation?.title}</DialogTitle>
        <DialogContent><DialogContentText>{confirmation?.text}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(null)}>Cancel</Button>
          <Button color='warning' variant='contained' onClick={() => { const execute = confirmation?.execute; setConfirmation(null); execute?.(); }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
